import type { ComponentPropsWithRef, MouseEvent, ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';
import { CloseIcon } from '@/shared-ui/icons';

const badgeVariants = cva('inline-flex items-center gap-2 font-sans whitespace-nowrap', {
    variants: {
        shape: {
            badge: 'rounded-sm px-2 py-1.5 text-[12px] leading-4 font-normal tracking-[0.02em]',
            pill: 'rounded-full px-3 py-1.5 text-[14px] leading-[18px] font-light bg-snk-cream text-snk-black',
        },
        type: {
            gwp: 'bg-snk-warm-neutral-pink text-snk-black',
            ndulge: 'bg-snk-ndulge text-snk-black',
            social: 'bg-snk-cream text-snk-black',
            custom: '',
        },
    },
    defaultVariants: {
        shape: 'badge',
        type: 'social',
    },
});

type BadgeVariants = VariantProps<typeof badgeVariants>;

export interface BadgeProps extends ComponentPropsWithRef<'span'>, BadgeVariants {
    children: ReactNode;
    onDismiss?: () => void;
    dismissLabel?: string;
}

export const Badge = ({
    children,
    shape,
    type,
    className,
    onDismiss,
    dismissLabel = 'Dismiss',
    ref,
    ...props
}: BadgeProps) => {
    const resolvedShape = shape ?? 'badge';
    const isPill = resolvedShape === 'pill';
    const effectiveType = isPill ? null : (type ?? 'social');
    const iconSizeClass = isPill ? 'size-3' : 'size-2.5';

    const handleDismiss = (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        onDismiss?.();
    };

    return (
        <span
            ref={ref}
            data-slot="badge"
            data-shape={resolvedShape}
            data-type={effectiveType ?? undefined}
            className={cn(badgeVariants({ shape, type: effectiveType }), className)}
            {...props}>
            <span data-slot="badge-label">{children}</span>
            {onDismiss && (
                <button
                    type="button"
                    aria-label={dismissLabel}
                    data-slot="badge-dismiss"
                    onClick={handleDismiss}
                    className={cn(
                        'inline-flex shrink-0 cursor-pointer items-center justify-center rounded-full text-current outline-none focus-visible:ring-2 focus-visible:ring-current/40',
                        iconSizeClass
                    )}>
                    <CloseIcon className={iconSizeClass} aria-hidden="true" />
                </button>
            )}
        </span>
    );
};
